import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { mealPlansAPI, vendorsAPI } from "../../services/api";
import MealPlanForm from "../Vendor/MealPlanForm";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/LoadingSpinner";

const EditMealPlan = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [selectedVendorId, setSelectedVendorId] = useState(null);

  // Fetch vendors for dropdown
  const { data: vendorsData, isLoading: vendorsLoading } = useQuery(
    "vendors",
    () => vendorsAPI.getVendors(),
    { keepPreviousData: true }
  );

  const vendors = vendorsData?.data?.data?.vendors || [];

  // Fetch the meal plan to edit
  // Use the same endpoint structure as vendor for consistency
  const {
    data: mealPlanData,
    isLoading: mealPlanLoading,
    error,
  } = useQuery(["adminMealPlan", id], () => mealPlansAPI.getMealPlan(id), {
    enabled: !!id,
    retry: false,
    onSuccess: (data) => {
      // Extract vendor ID from meal plan
      const mealPlan = data?.data?.data?.mealPlan;
      if (mealPlan?.vendorId || mealPlan?.vendor_id) {
        setSelectedVendorId(mealPlan.vendorId || mealPlan.vendor_id);
      }
    },
  });

  const updateMealPlanMutation = useMutation(
    (data) => mealPlansAPI.updateMealPlan(id, data),
    {
      onSuccess: (data) => {
        toast.success("Meal plan updated successfully!");
        queryClient.invalidateQueries("admin-meal-plans");
        queryClient.invalidateQueries(["adminMealPlan", id]);
        navigate("/admin/meal-plans");
      },
      onError: (error) => {
        console.error("Error updating meal plan:", error);
        toast.error(
          error.response?.data?.message || "Failed to update meal plan"
        );
      },
    }
  );

  const handleSave = async (formData) => {
    try {
      await updateMealPlanMutation.mutateAsync(formData);
    } catch (error) {
      // Error is handled by the mutation's onError callback
      throw error;
    }
  };

  const handleCancel = () => {
    navigate("/admin/meal-plans");
  };

  const handleVendorChange = (vendorId) => {
    setSelectedVendorId(vendorId);
  };

  if (vendorsLoading || mealPlanLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h3>Error loading meal plan</h3>
        <p>{error.message || "Failed to load meal plan"}</p>
        <button onClick={() => navigate("/admin/meal-plans")}>
          Back to Meal Plans
        </button>
      </div>
    );
  }

  if (!mealPlanData?.data?.data?.mealPlan) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h3>Meal plan not found</h3>
        <p>
          The meal plan you're looking for doesn't exist or you don't have
          permission to edit it.
        </p>
        <button onClick={() => navigate("/admin/meal-plans")}>
          Back to Meal Plans
        </button>
      </div>
    );
  }

  // Transform the data structure to match what MealPlanForm expects
  // The public endpoint returns { mealPlan, items } but MealPlanForm expects mealPlan.items
  const rawMealPlan = mealPlanData.data.data.mealPlan;
  const rawItems = mealPlanData.data.data.items || [];

  console.log("Raw meal plan data:", rawMealPlan);
  console.log("Raw items data:", rawItems);

  // Normalize boolean values - the public endpoint already normalizes them, but we ensure consistency
  const offersDelivery =
    rawMealPlan.offersDelivery === true ||
    rawMealPlan.offersDelivery === "true" ||
    rawMealPlan.offersDelivery === 1 ||
    rawMealPlan.offers_delivery === true ||
    rawMealPlan.offers_delivery === "true" ||
    rawMealPlan.offers_delivery === 1 ||
    true; // Default to true
  const offersPickup =
    rawMealPlan.offersPickup === true ||
    rawMealPlan.offersPickup === "true" ||
    rawMealPlan.offersPickup === 1 ||
    rawMealPlan.offers_pickup === true ||
    rawMealPlan.offers_pickup === "true" ||
    rawMealPlan.offers_pickup === 1 ||
    false;

  // Transform the meal plan to match the vendor endpoint format exactly
  // This ensures the form receives data in the same structure as vendor edit form
  const mealPlan = {
    id: rawMealPlan.id,
    vendorId: rawMealPlan.vendorId || rawMealPlan.vendor_id,
    vendor_id: rawMealPlan.vendorId || rawMealPlan.vendor_id,
    title: rawMealPlan.title || "",
    description: rawMealPlan.description || "",
    // Price fields - handle both camelCase and snake_case
    price: parseFloat(rawMealPlan.price || rawMealPlan.base_price || 0),
    basePrice: parseFloat(rawMealPlan.basePrice || rawMealPlan.base_price || rawMealPlan.price || 0),
    base_price: parseFloat(rawMealPlan.basePrice || rawMealPlan.base_price || rawMealPlan.price || 0),
    deliveryFee: parseFloat(rawMealPlan.deliveryFee || rawMealPlan.delivery_fee || 0),
    delivery_fee: parseFloat(rawMealPlan.deliveryFee || rawMealPlan.delivery_fee || 0),
    // Plan type and duration
    planType: rawMealPlan.planType || rawMealPlan.plan_type || "weekly",
    plan_type: rawMealPlan.planType || rawMealPlan.plan_type || "weekly",
    durationDays: rawMealPlan.durationDays || rawMealPlan.duration_days || 7,
    duration_days: rawMealPlan.durationDays || rawMealPlan.duration_days || 7,
    maxSubscribers: rawMealPlan.maxSubscribers || rawMealPlan.max_subscribers || 50,
    max_subscribers: rawMealPlan.maxSubscribers || rawMealPlan.max_subscribers || 50,
    // Image
    imageUrl: rawMealPlan.imageUrl || rawMealPlan.image_url || null,
    image_url: rawMealPlan.imageUrl || rawMealPlan.image_url || null,
    // Fulfillment options - normalized booleans
    offersDelivery: offersDelivery,
    offers_delivery: offersDelivery,
    offersPickup: offersPickup,
    offers_pickup: offersPickup,
    // Pickup details
    pickupAddress: rawMealPlan.pickupAddress || rawMealPlan.pickup_address || "",
    pickup_address: rawMealPlan.pickupAddress || rawMealPlan.pickup_address || "",
    pickupLatitude: rawMealPlan.pickupLatitude || rawMealPlan.pickup_latitude
      ? parseFloat(rawMealPlan.pickupLatitude || rawMealPlan.pickup_latitude)
      : null,
    pickup_latitude: rawMealPlan.pickupLatitude || rawMealPlan.pickup_latitude
      ? parseFloat(rawMealPlan.pickupLatitude || rawMealPlan.pickup_latitude)
      : null,
    pickupLongitude: rawMealPlan.pickupLongitude || rawMealPlan.pickup_longitude
      ? parseFloat(rawMealPlan.pickupLongitude || rawMealPlan.pickup_longitude)
      : null,
    pickup_longitude: rawMealPlan.pickupLongitude || rawMealPlan.pickup_longitude
      ? parseFloat(rawMealPlan.pickupLongitude || rawMealPlan.pickup_longitude)
      : null,
    pickupInstructions: rawMealPlan.pickupInstructions || rawMealPlan.pickup_instructions || "",
    pickup_instructions: rawMealPlan.pickupInstructions || rawMealPlan.pickup_instructions || "",
    // Status
    isActive: rawMealPlan.isActive !== undefined ? rawMealPlan.isActive : rawMealPlan.is_active,
    is_active: rawMealPlan.isActive !== undefined ? rawMealPlan.isActive : rawMealPlan.is_active,
    // Include items array that MealPlanForm expects - match vendor endpoint format
    items: rawItems.map((item) => ({
      id: item.id,
      day_of_week: item.day_of_week,
      meal_name: item.meal_name,
      description: item.description || "",
      // Handle ingredients - prefer ingredientIds/ingredientsFromTable, fallback to ingredients array
      ingredients: item.ingredients || [],
      ingredientIds: item.ingredientIds || [],
      ingredientsFromTable: item.ingredientsFromTable || [],
      allergens: item.allergens || [],
      allergensFromTable: item.allergensFromTable || [],
      calories: item.calories || null,
      protein: item.protein || null,
      carbs: item.carbs || null,
      fat: item.fat || null,
      image_url: item.image_url || null,
    })),
  };

  console.log("Transformed meal plan with items:", mealPlan);

  return (
    <MealPlanForm
      mealPlan={mealPlan}
      onSave={handleSave}
      onCancel={handleCancel}
      isAdmin={true}
      selectedVendorId={selectedVendorId || mealPlan.vendor_id}
      onVendorChange={handleVendorChange}
      vendors={vendors}
    />
  );
};

export default EditMealPlan;
